import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { cn } from '@/lib/utils'
import { NoDataIllustration } from './no-data-illustration'

export interface NoDataFoundProps {
  /** Headline under the illustration. */
  title?: ReactNode
  /** Optional secondary line. */
  description?: ReactNode
  /** Optional action (button/link) rendered below the text. */
  action?: ReactNode
  className?: string
  /** 'sm' for compact contexts (dropdowns, small cards, side panels). */
  size?: 'sm' | 'md'
}

/**
 * The one "no data" look for the whole app: sad-folder illustration +
 * "No data found!". Use it for every empty list / table / card body.
 * For table bodies wrap it in `<TableCell colSpan={n}>`.
 */
export function NoDataFound({
  title = 'No data found!',
  description,
  action,
  className,
  size = 'md',
}: NoDataFoundProps) {
  const sm = size === 'sm'
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center text-center animate-fade-in',
        sm ? 'gap-2 px-3 py-6' : 'gap-4 px-4 py-12',
        className,
      )}
    >
      <NoDataIllustration size={sm ? 64 : 100} />
      <div className="flex flex-col items-center">
        <span className={cn('heading-text font-semibold', sm ? 'text-sm' : 'text-base')}>
          {title}
        </span>
        {description && (
          <div className={cn('mt-1 max-w-sm text-muted-foreground', sm ? 'text-xs' : 'text-sm')}>
            {description}
          </div>
        )}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}

function findScrollX(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node && node !== document.body) {
    const ox = getComputedStyle(node).overflowX
    if (ox === 'auto' || ox === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/**
 * Box (px) the empty-row content should take so it stays centred in the
 * VISIBLE part of a horizontally-scrolling table wrapper — or `null` when the
 * table fits its wrapper (desktop / wide screens), in which case the content
 * simply spans the full cell as before (visually unchanged).
 */
function useVisibleScrollWidth(cellRef: RefObject<HTMLTableCellElement | null>) {
  const [box, setBox] = useState<{ width: number; left: number } | null>(null)
  useLayoutEffect(() => {
    const cell = cellRef.current
    const scroller = findScrollX(cell)
    if (!cell || !scroller) return
    const table = cell.closest('table')
    const measure = () => {
      if (scroller.scrollWidth <= scroller.clientWidth + 1) {
        setBox(null)
        return
      }
      const cs = getComputedStyle(cell)
      const pl = parseFloat(cs.paddingLeft) || 0
      const pr = parseFloat(cs.paddingRight) || 0
      const width = Math.max(0, scroller.clientWidth - pl - pr)
      setBox((prev) => (prev && prev.width === width && prev.left === pl ? prev : { width, left: pl }))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(scroller)
    if (table) ro.observe(table)
    return () => ro.disconnect()
  }, [cellRef])
  return box
}

/**
 * Full-width `<tr>` for an empty table body. Works with both the shared
 * `<Table>` primitives and raw `<table className="table-default">` markup.
 *
 * When the table is wider than its overflow-x wrapper (phones), the content is
 * pinned (`position: sticky`, left = cell padding) and sized to the wrapper's visible width so the
 * illustration stays centred on screen instead of across the full table.
 */
export function TableEmptyRow({
  colSpan,
  ...props
}: NoDataFoundProps & { colSpan: number }) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const box = useVisibleScrollWidth(cellRef)
  return (
    <tr className="table-empty-row">
      <td ref={cellRef} colSpan={colSpan}>
        <div
          className={box ? 'sticky' : undefined}
          style={box ? { width: box.width, left: box.left } : undefined}
        >
          <NoDataFound {...props} />
        </div>
      </td>
    </tr>
  )
}
